import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({ name: 'chatMarkdown' })
export class ChatMarkdownPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string | null | undefined): SafeHtml {
    if (!value) return '';
    return this.sanitizer.bypassSecurityTrustHtml(this.parse(value));
  }

  private parse(md: string): string {
    // ── 1. XSS escape (done first so all later replacements work on safe text) ──
    let html = md
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // ── 2. Fenced code blocks (protect from further processing) ─────────────────
    const codeBlocks: string[] = [];
    html = html.replace(/```(\w*)\n?([\s\S]*?)```/g, (_m, lang, code) => {
      const placeholder = `\x02CODE${codeBlocks.length}\x03`;
      codeBlocks.push(
        '<pre class="chat-code-block"><code class="language-' +
        (lang || 'plaintext') + '">' + code.trim() + '</code></pre>'
      );
      return placeholder;
    });

    // ── 3. Markdown tables ───────────────────────────────────────────────────────
    // Matches a block of lines that all start with '|'
    // Line 1  = header row:   | A | B | C |
    // Line 2  = separator:    |---|---|---|  (required by GFM spec)
    // Line 3+ = data rows:    | x | y | z |
    html = html.replace(
      /^(\|.+)\n(\|[-| :]+)\n((?:\|.+\n?)+)/gm,
      (_match, headerLine: string, _sepLine: string, bodyLines: string) => {

        // Parse a single pipe-row into cell strings
        const parseCells = (line: string): string[] =>
          line.replace(/^\||\|$/g, '').split('|').map(c => c.trim());

        const headers = parseCells(headerLine);
        const rows    = bodyLines.trim().split('\n').map(l => parseCells(l));

        const thHtml = headers
          .map(h => `<th>${h}</th>`)
          .join('');

        const tbodyHtml = rows
          .map(cells => {
            const tdHtml = cells
              .map(c => `<td>${c}</td>`)
              .join('');
            return `<tr>${tdHtml}</tr>`;
          })
          .join('');

        return (
          '<div class="chat-table-wrap">' +
            '<table class="chat-table">' +
              '<thead><tr>' + thHtml + '</tr></thead>' +
              '<tbody>' + tbodyHtml + '</tbody>' +
            '</table>' +
          '</div>'
        );
      }
    );

    // ── 4. Headings ──────────────────────────────────────────────────────────────
    html = html.replace(/^### (.+)$/gm, '<h3 class="chat-h3">$1</h3>');
    html = html.replace(/^## (.+)$/gm,  '<h2 class="chat-h2">$1</h2>');
    html = html.replace(/^# (.+)$/gm,   '<h1 class="chat-h1">$1</h1>');

    // ── 5. HR ────────────────────────────────────────────────────────────────────
    html = html.replace(/^[-*_]{3,}$/gm, '<hr class="chat-hr">');

    // ── 6. Blockquote ────────────────────────────────────────────────────────────
    html = html.replace(/^&gt; (.+)$/gm, '<blockquote class="chat-bq">$1</blockquote>');

    // ── 7. Inline emphasis ───────────────────────────────────────────────────────
    html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
    html = html.replace(/\*\*(.+?)\*\*/g,     '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g,         '<em>$1</em>');
    html = html.replace(/__(.+?)__/g,         '<strong>$1</strong>');
    // Avoid stripping *(no IMSI)* style italic-wrapped text that already went through em
    html = html.replace(/_([^_\n]+)_/g,       '<em>$1</em>');

    // ── 8. Inline code ───────────────────────────────────────────────────────────
    html = html.replace(/`([^`]+)`/g, '<code class="chat-inline-code">$1</code>');

    // ── 9. Links ─────────────────────────────────────────────────────────────────
    html = html.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="chat-link">$1</a>'
    );

    // ── 10. Unordered lists ──────────────────────────────────────────────────────
    html = html.replace(/^[\-\*\+] (.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>[\s\S]*?<\/li>\n?)+/g, m => '<ul class="chat-ul">' + m + '</ul>');

    // ── 11. Ordered lists ────────────────────────────────────────────────────────
    html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

    // ── 12. Paragraphs & line breaks ─────────────────────────────────────────────
    html = html.replace(/\n{2,}/g, '</p><p>');
    html = '<p>' + html + '</p>';
    html = html.replace(/([^>])\n([^<])/g, '$1<br>$2');
    html = html.replace(/<p>\s*<\/p>/g, '');

    // ── 13. Unwrap block elements from <p> ───────────────────────────────────────
    const blockTags = ['pre', 'ul', 'ol', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'hr'];
    blockTags.forEach(tag => {
      html = html.replace(new RegExp('<p>(<' + tag + '[\\s>])', 'g'),  '$1');
      html = html.replace(new RegExp('(<\\/' + tag + '>)<\\/p>', 'g'), '$1');
    });

    // ── 14. Restore code blocks ──────────────────────────────────────────────────
    codeBlocks.forEach((block, i) => {
      html = html.replace(`\x02CODE${i}\x03`, block);
    });

    return html;
  }
}